import * as React from "react";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";

import { cn } from "@/lib/utils";

/**
 * HoverCard Component
 *
 * A card that appears on hover, typically used to show additional information
 * about an element without requiring a click.
 *
 * @example
 * ```tsx
 * <HoverCard>
 *   <HoverCardTrigger>Hover me</HoverCardTrigger>
 *   <HoverCardContent>
 *     <p>Additional information here</p>
 *   </HoverCardContent>
 * </HoverCard>
 * ```
 */
function HoverCard({ ...props }: React.ComponentProps<typeof HoverCardPrimitive.Root>) {
    return <HoverCardPrimitive.Root data-slot="hover-card" {...props} />;
}
HoverCard.displayName = "HoverCard";

/**
 * HoverCardTrigger Component
 *
 * The element that triggers the hover card to appear.
 */
function HoverCardTrigger({ ...props }: React.ComponentProps<typeof HoverCardPrimitive.Trigger>) {
    return <HoverCardPrimitive.Trigger data-slot="hover-card-trigger" {...props} />;
}
HoverCardTrigger.displayName = "HoverCardTrigger";

/**
 * HoverCardContent Component
 *
 * The content that appears in the hover card.
 */
function HoverCardContent({
    className,
    align = "center",
    sideOffset = 4,
    ...props
}: React.ComponentProps<typeof HoverCardPrimitive.Content>) {
    return (
        <HoverCardPrimitive.Portal data-slot="hover-card-portal">
            <HoverCardPrimitive.Content
                data-slot="hover-card-content"
                align={align}
                sideOffset={sideOffset}
                className={cn(
                    "setu:bg-popover setu:text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 setu:z-50 setu:w-64 setu:origin-(--radix-hover-card-content-transform-origin) setu:rounded-md setu:!border setu:p-4 setu:shadow-md setu:outline-hidden",
                    className
                )}
                {...props}
            />
        </HoverCardPrimitive.Portal>
    );
}
HoverCardContent.displayName = "HoverCardContent";

export { HoverCard, HoverCardTrigger, HoverCardContent };
